import jsPDF from "jspdf";
import { autoTable } from 'jspdf-autotable';
import moment from "moment";
import visionLogo from "../../images/vision-logo.png";
import { calculateCrates } from "../helpers/print";
import { formatAmount } from "../helpers/common";
import { downloadPdf } from "../helpers/pdfs";

export const generateInvoicePdf = async (invoiceData, isEmail = false) => {
    const doc = new jsPDF('p', 'mm', 'a4');

    const fileName = `Vision Product Invoice ${invoiceData?.InvoiceNo}_${invoiceData?.tblCustomer?.CustomerCode}`

    // Delivery Instructions
    doc.setFontSize(10);
    doc.setFont("primary", "bold")
    doc.text("Delivery Instructions :", 10, 48)
    doc.setFont("helvetica", "normal");
    const maxWidthDelivery = 150;
    const DeliveryInstructions = invoiceData?.DeliveryInstructions
    let wrappedDeliveryText = doc.splitTextToSize(DeliveryInstructions, maxWidthDelivery);
    doc.text(wrappedDeliveryText, 45, 48);

    // Variables Declarations 
    const products = invoiceData.tblOrderDetails;
    const cratesDetails = await calculateCrates(invoiceData?.tblOrderDetails);
    const showRRP = invoiceData?.tblCustomer?.RRP ? true : false;
    let productsTableBody = [];
    let productsTableHead = [["Code", "Qty", "Type", "Product Details", "Cnt/ Wt", "Unit Price", "Value", "GST"]];
    let crateDeposit = 0;
    let crateHireFee = 0;
    let crateHireGST = 0;
    let subTotal = 0;
    let totalGST = 0;
    let crateTotal = 0;

    let productsColumnStyles = {
        0: { cellWidth: 12, halign: "center", },
        1: { cellWidth: 10, halign: "center" },
        2: { cellWidth: 15, halign: "left" },
        3: { cellWidth: 90, halign: "left" },
        4: { cellWidth: 15, halign: "center" },
        5: { cellWidth: 17, halign: "right" },
        6: { cellWidth: 20, halign: "right" },
        7: { cellWidth: 17, halign: "right" },
    }

    // Create Data for Table Body

    const sortProducts = products.sort((a, b) => a.ProductName.localeCompare(b.ProductName));

    sortProducts.map((product, productIndex) => {
        let type = product.tblContainer?.TypeInCode != "Un" ? product.tblContainer?.TypeInCode : "";
        let ProductName = ` ${product.ProductName} ${product.ClassName || ''} ${product.SizeName || ''}`;
        ProductName = ProductName?.replace(/\s+/g, ' ').trim();
        let lineText = product.Qty == 0 ? "NOT AVAILABLE" : product.SellPrice == 0 && product.ExtSell == 0 ? "NO CHARGE" : ""
        subTotal += product.ExtSell;
        totalGST += product.GSTAmount;
        crateDeposit += product.CrateValue;
        crateHireFee += product.CrateHire;
        crateHireGST += product.CrateGST;

        if (lineText) {
            productsTableBody.push([
                { content: product.ProductCode },
                { content: product.Qty },
                { content: type },
                { content: ProductName },
                { content: lineText, colSpan: showRRP ? 5 : 4, styles: { halign: "center", fontStyle: "bold" } },
            ]);
        } else {
            const sellPriceStyles = {
                ...(product.LastSellPU > 0 && {
                    fontStyle: product.SellPrice > product.LastSellPU ? 'bold' : 'underline',
                    fillColor: product.SellPrice > product.LastSellPU
                        ? [255, 200, 200]   // red for increase
                        : [204, 255, 204]   // green for decrease
                })
            };
            productsTableBody.push([product.ProductCode, product.Qty, type, ProductName, product.Count, { content: formatAmount(product.SellPrice), styles: sellPriceStyles }, formatAmount(product.ExtSell), formatAmount(product.GSTAmount)])
        }
    });

    // Create Data for Table Head
    if (showRRP) {
        productsTableHead[0]?.splice(5, 0, "RRP");

        productsTableBody.map((item, index) => {
            let productRRP = products[index]?.RRP || 0
            item.splice(5, 0, productRRP)
        });

        productsColumnStyles = {
            0: { cellWidth: 12, halign: "center" },
            1: { cellWidth: 10, halign: "center" },
            2: { cellWidth: 15, halign: "left" },
            3: { cellWidth: 83, halign: "left" },
            4: { cellWidth: 12, halign: "center" },
            5: { cellWidth: 15, halign: "center" },
            6: { cellWidth: 17, halign: "right" },
            7: { cellWidth: 20, halign: "right" },
            8: { cellWidth: 16, halign: "right" },
        }
    }

    const pageHeight = doc.internal.pageSize.height;

    // Product Details Table
    autoTable(doc, {
        startY: 50,
        columnStyles: productsColumnStyles,
        head: productsTableHead,
        body: productsTableBody,
        theme: "plain",
        headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            halign: "center",
            lineWidth: 0.2
        },
        didDrawCell: function (data) {
            const colIndex = showRRP ? 6 : 5;

            if (data.section === 'body' && data.column?.index === colIndex) {
                const cellData = data?.cell?.raw;

                // Only for increased price
                if (cellData?.styles?.fontStyle === 'underline') {
                    const { x, y, width, height } = data.cell;
                    const doc = data.doc;

                    // Draw underline
                    doc.setDrawColor(0);
                    doc.setLineWidth(0.2);
                    doc.line(x + 2, y + height - 1, x + width, y + height - 1);
                }
            }
        },
        didDrawPage: (data) => {
            // HEADER
            Header(invoiceData, doc);

            // FOOTER
            Footer(doc);
        },
        styles: { fontSize: 9, cellPadding: 1 },
        margin: { top: 45, bottom: 55, left: showRRP ? 6 : 7, right: 7 },
        showHead: "everyPage",
    });

    // Check End of the Page and Move to new page if there no required space left.
    let yPosition = doc.lastAutoTable.finalY + 5;

    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(10, yPosition - 3, doc.internal.pageSize.width - 8, yPosition - 3);

    if (yPosition + 80 > pageHeight) {
        doc.addPage();

        // HEADER
        Header(invoiceData, doc);

        // FOOTER
        Footer(doc);
        yPosition = 45;
        doc.line(10, yPosition - 3, doc.internal.pageSize.width - 8, yPosition - 3);
    }

    // Crate Summary
    doc.setFontSize(10);

    let crateSummaryBody = [];
    Object.keys(cratesDetails).filter((key) => {
        let crateItem = cratesDetails[key];
        if (crateItem.label != "Un") {
            crateTotal += crateItem.totalPrice;
            crateSummaryBody.push([crateItem.description, crateItem.qty, crateItem.label, formatAmount(crateItem.totalPrice)]);
        }
    })

    if (crateSummaryBody.length > 0) {
        crateSummaryBody.push([
            { content: "Crate Total :", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
            { content: `${formatAmount(crateTotal)}`, styles: { fontStyle: "bold" } },
        ]);

        autoTable(doc, {
            startY: yPosition,
            head: [[
                { content: "Crate Summary", colSpan: 4, styles: { fontSize: 14, fillColor: [211, 211, 211], fontStyle: "bold" } },
            ]],
            body: crateSummaryBody,
            theme: "plain",
            margin: { left: 8 },
            styles: { fontSize: 8, cellPadding: 1 },
            columnStyles: { 0: { cellWidth: 30, halign: "left" }, 1: { cellWidth: 15, halign: "center" }, 2: { cellWidth: 15, halign: "right" }, 3: { cellWidth: 20, halign: "right" } },
            didDrawPage: function (data) {

                const rowCount = crateSummaryBody.length;
                const totalRowHeight = data.table.body[rowCount - 1].height;
                const separatorY = data.cursor.y - totalRowHeight;

                doc.setDrawColor(0);
                doc.setLineWidth(0.5);
                doc.line(8, separatorY, doc.internal.pageSize.getWidth() - 122, separatorY);
            }
        });
    }

    // Invoice Total Summary
    let lessRebate = subTotal * (invoiceData?.tblCustomer?.StandardRebate / 100);

    let totalSummaryBody = [
        ["Produce Subtotal:", `${formatAmount(subTotal)}`, `${formatAmount(totalGST)}`],
        ["Plus Crate Deposit:", `${formatAmount(crateHireFee)}`, ""],
        ["Plus CrateHire Fee:", `${formatAmount(crateDeposit - crateHireFee)}`, ""],
        ["Plus Crate Hire GST:", `${formatAmount(crateHireGST)}`, ""]
    ]

    if (invoiceData?.tblCustomer?.StandardRebate && invoiceData?.tblCustomer?.StandardRebate > 0) {
        totalSummaryBody.splice(1, 0, [`Less Produce Rebate at ${invoiceData?.tblCustomer?.StandardRebate}% :`, `${formatAmount(lessRebate)}`, ""]);
        totalSummaryBody.splice(2, 0, ["Produce Total:", `${formatAmount(subTotal - lessRebate)}`, ""]);
    }

    totalSummaryBody.push([
        { content: "INVOICE TOTAL (incGST) :", colSpan: 1, styles: { halign: "right", fontStyle: "bold" } },
        { content: `${formatAmount((subTotal - lessRebate) + crateDeposit + crateHireGST)}`, styles: { fontStyle: "bold" } }
    ])

    autoTable(doc, {
        startY: yPosition,
        body: totalSummaryBody,
        theme: "plain",
        margin: { left: 113 },
        styles: { fontSize: 10, cellPadding: 1, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 52, halign: "right" }, 1: { cellWidth: 22, halign: "right" }, 2: { cellWidth: 18, halign: "right" } },
        didDrawPage: function (data) {

            const rowCount = totalSummaryBody.length;
            const totalRowHeight = data.table.body[rowCount - 1].height;
            const separatorY = data.cursor.y - totalRowHeight;

            doc.setDrawColor(100);
            doc.setLineWidth(0.3);
            doc.line(100, separatorY, doc.internal.pageSize.getWidth() - 10, separatorY);

            doc.setDrawColor(0);
            doc.setLineWidth(0.5);
            doc.line(100, separatorY + 6, doc.internal.pageSize.getWidth() - 10, separatorY + 6);
        }
    });

    // Footer Pagination
    const totalPageCount = doc.internal.pages.length - 1;

    for (let i = 1; i <= totalPageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor("#787a80")
        const pageText = `Page ${i} of ${totalPageCount}`;
        doc.text(
            pageText,
            doc.internal.pageSize.getWidth() - 25,
            doc.internal.pageSize.getHeight() - 3,
        );
    }

    const pdf = doc.output('arraybuffer');
    if (isEmail) {
        return pdf;
    }
    downloadPdf(pdf, true, true, fileName);
}

const Header = (invoiceData, doc) => {
    doc.addImage(visionLogo, "PNG", 160, 7, 44, 12);
    doc.setFontSize(23);

    // Invoice Text
    doc.setFont("primary", "bold")
    doc.text("TAX INVOICE", 10, 15);

    // Customer / Invoice Details
    doc.setFontSize(10);
    doc.text(`${invoiceData?.tblCustomer?.CustomerName}`, 10, 30);
    let invoiceAddress = invoiceData?.tblCustomer?.InvoiceAddress1 || invoiceData.tblCustomer?.InvoiceAddress2 || invoiceData.tblCustomer?.InvoiceAddress3;
    doc.text(`${invoiceAddress}`, 10, 35);
    doc.text(`${invoiceData?.tblCustomer?.InvoicePostCode}`, 10, 40);
    doc.text("Invoice No :", 100, 30);
    doc.text(`${invoiceData.DisplayInvoiceNo}`, 120, 30);
    doc.text("Code :", 100, 35);
    doc.text(`${invoiceData.tblCustomer?.CustomerCode}`, 120, 35);
    doc.text("Date :", 140, 30);
    doc.text(`${moment(invoiceData.InvoiceDate, "YYYY-MM-DD").format("MM/DD/YYYY")}`, 160, 30);
    doc.text("PO :", 140, 35);
    doc.text(`${invoiceData.PurchaseOrder}`, 160, 35);
}

const Footer = (doc) => {
    doc.setFontSize(8);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(10, doc.internal.pageSize.height - 50, doc.internal.pageSize.width - 10, doc.internal.pageSize.height - 50);

    const maxWidth = 100;
    let startXText = doc.internal.pageSize.getWidth() - 200;
    let startYText = doc.internal.pageSize.getHeight() - 54;

    const leftTextArray = [
        "Where produce is marked with an Asterisk (*), the item attracts GST and the GST amount is shown. Items without an asterisk are GST exempt.",
        "All Claims for credit must be received within 24 hours of receipt of goods.",
        "Where the Sell Price is red and/or bold, your price has gone up since the last time you ordered this product.",
        "Where the Sell Price is green and/or underlined, your price has gone down since the last time you ordered this product.",
        "*All products are Australian grown unless specified on our invoice."
    ];

    leftTextArray.map((text, index) => {
        let wrappedText = doc.splitTextToSize(text, maxWidth);
        startYText += 10;
        if (index < 2) {
            doc.setFont("helvetica", "bold");
            doc.text(wrappedText, startXText, startYText);
            doc.setFont("helvetica", "normal");
        } else {
            doc.text(wrappedText, startXText, startYText);
        }

    });

    const rightTextArray = [
        "Vision Produce Pty Ltd",
        "ABN 11 098 952 768",
        "Mail Point 103, Market City,",
        "280 Bannister Road",
        "Canning Vale WA 6155",
        "Telephone: (08) 9456 5953",
        "Facsimile: (08) 9456 5948",
        "E-mail: accounts@visionproduce.com.au"
    ];

    startXText = doc.internal.pageSize.getWidth() - 70;
    startYText = doc.internal.pageSize.getHeight() - 40;
    rightTextArray.map((text, index) => {
        startYText += 4;
        if (index == 0) {
            doc.setFont("helvetica", "bold");
            doc.text(text, startXText, startYText);
            doc.setFont("helvetica", "normal");
        } else {
            doc.text(text, startXText, startYText);
        }

    })
}